import useTranslation from '../../../../Hooks/useTranslation';
import {
	ToothRestoration,
	ToothRestorationCrownBase,
	ToothRestorationCrownType,
	ToothRestorationDetail,
	ToothRestorationImplantType,
	ToothRestorationMaterial,
	ToothRestorationQuality,
} from '../../../../graphql/graphql';

type SimpleRestoration = Omit<
	ToothRestoration,
	'type' | 'surfaces' | '__typename' | 'createdAt'
>;
export type SimpleRestorationKey = keyof SimpleRestoration;
type SimpleRestorationValue =
	| ToothRestorationMaterial
	| ToothRestorationDetail
	| ToothRestorationCrownType
	| ToothRestorationCrownBase
	| ToothRestorationImplantType
	| ToothRestorationQuality;

const SIMPLE_RESTORATION_KEY_MAP: Record<SimpleRestorationKey, string> = {
	material:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.title',
	crownType:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.title',
	crownBase:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownBase.title',
	implantType:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.implantType.title',
	quality:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.quality.title',
	detail:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.detail.title',
};

const SIMPLE_RESTORATION_VALUE_MAP: Record<SimpleRestorationValue, string> = {
	[ToothRestorationMaterial.Gold]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.gold',
	[ToothRestorationMaterial.NonPreciousMaterial]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.nonPreciousMaterial',
	[ToothRestorationMaterial.Ceramic]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.ceramic',
	[ToothRestorationMaterial.Composite]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.composite',
	[ToothRestorationMaterial.Amalgam]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.amalgam',
	[ToothRestorationMaterial.Temporary]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.temporary',
	[ToothRestorationMaterial.GlassIonomer]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.glassIonomer',
	[ToothRestorationMaterial.MetalCeramic]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.material.metalCeramic',
	[ToothRestorationCrownType.SingleCrown]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.singleCrown',
	[ToothRestorationCrownType.Pontic]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.pontic',
	[ToothRestorationCrownType.Abutment]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.abutment',
	[ToothRestorationCrownBase.Implant]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownBase.implant',
	[ToothRestorationCrownBase.Natural]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.crownBase.natural',
	[ToothRestorationImplantType.TissueLevel]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.implantType.tissueLevel',
	[ToothRestorationImplantType.BoneLevel]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.implantType.boneLevel',
	[ToothRestorationQuality.Sufficient]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.quality.sufficient',
	[ToothRestorationQuality.Insufficient]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.quality.insufficient',
	[ToothRestorationQuality.Uncertain]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.quality.uncertain',
	[ToothRestorationDetail.Flush]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.detail.flush',
	[ToothRestorationDetail.Overhang]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.detail.overhang',
	[ToothRestorationDetail.Shortfall]:
		'patientTreatment.meddiDENT.dentalCross.restorationModal.detail.shortfall',
};

export default function parseSimpleRestorationValue<
	T extends SimpleRestorationKey,
>(key: T, value: NonNullable<SimpleRestoration[T]>) {
	const { t } = useTranslation();
	return `${t(SIMPLE_RESTORATION_KEY_MAP[key])} ${t(
		SIMPLE_RESTORATION_VALUE_MAP[value],
	)}`;
}
